import { FC, ReactElement, useEffect } from 'react';
import { Link } from 'react-router-dom';
import urlSlug from 'url-slug';
import { gql, useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone } from '@fortawesome/free-solid-svg-icons/faPhone';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope';
import cx from 'classnames';
import useWindowSize from '../hooks/useWindowSize';
import { ReactComponent as Person } from '../assets/icons/person.svg';
import Button from './Button';
import Agent from '../models/Agent';
import styles from './PersonCard.module.scss';
import { Skeleton } from './Skeleton';
import { ZeroWidthSpace } from './ZeroWidthSpace';
import { useSerializedState } from '../hooks/useSerialized';
import Image from './Image';
import { useAmplitude } from '../amplitude/amplitudeContext';

export interface PersonCardProps {
  id?: string;
  featured?: boolean;
  test?: string;
}

const GetAgent = gql`
  query GetAgent($id: ID!) {
    agent(id: $id) {
      asab
      cityState
      email
      firstName
      lastName
      id
      phoneNumber
      photo
      state
      stateBroker
      preferredName
      title
    }
  }
`;

const PersonCard: FC<PersonCardProps> = ({ id, featured }) => {
  const [loadAgent, { data, loading, error }] = useLazyQuery<{ agent: Agent }, { id: string }>(GetAgent);
  const [agent, setAgent] = useSerializedState(Agent, data?.agent);

  const rawAgent = data?.agent;
  useEffect(() => {
    setAgent(rawAgent);
  }, [rawAgent, setAgent]);

  const [, width] = useWindowSize();
  const { t } = useTranslation();

  const agentUrl = `/agent/${data?.agent.firstName ? urlSlug(data?.agent.firstName) : null}-${data?.agent.lastName ? urlSlug(data?.agent.lastName) : null}-${id}`;

  useEffect(() => {
    if (id) {
      loadAgent({ variables: { id } });
    }
  }, [id, loadAgent]);

  const skeleton = !data || loading || !id;

  const { logAmplitudeEvent } = useAmplitude();

  /* istanbul ignore if */
  if (error) {
    return <div>something went wrong</div>;
  }

  function Information(): ReactElement {
    if (width > 700) {
      return skeleton ? (
        <Skeleton className={styles.point} count={2} />
      ) : (
        <>
          <div className={styles.point} onClick={(e) => logAmplitudeEvent('selects the agent email to send a message')} aria-hidden="true" data-test="email">
            {data?.agent.email || <ZeroWidthSpace />}
          </div>
          <div className={styles.point} onClick={(e) => logAmplitudeEvent('selects the agent phone number to call')} aria-hidden="true" data-test="phone-number">
            {data?.agent.phoneNumber || <ZeroWidthSpace />}
          </div>
        </>
      );
    }

    return (
      <div className={styles.buttonContainer}>
        <Button
          stopPropagation
          className={styles.button}
          onClick={(e) => logAmplitudeEvent('selects the agent phone icon to call on mobile view')}
          href={`tel:+1-${data?.agent.phoneNumber}`}
          size="small"
          secondary
        >
          <FontAwesomeIcon icon={faPhone} style={{ transform: 'scale(-1, 1)' }} />
          {t('Call')}
        </Button>

        <Button stopPropagation className={styles.button} href={`mailto:${data?.agent.email}`} size="small" secondary>
          <FontAwesomeIcon icon={faEnvelope} />
          {t('Email')}
        </Button>
      </div>
    );
  }

  const location = agent?.isBroker() ? agent.title : data?.agent.cityState;

  return (
    <Link
      className={styles.personCard}
      onClick={() => {
        if (agent?.isBroker()) {
          if (featured) {
            logAmplitudeEvent('selects a broker from the Featured Brokers page of results');
          } else {
            logAmplitudeEvent('selects a broker from the Brokers page of results');
          }
        } else if (featured) {
          logAmplitudeEvent('selects an agent from the Agent Featured page of results');
        } else {
          logAmplitudeEvent('selects an agent from the Agents page of results');
        }
      }}
      data-test="agent-profile-link"
      to={agentUrl}
    >
      <Image alt={agent?.fullName} className={styles.photo} fallBack={Person} src={data?.agent.photo} />

      <div className={styles.info}>
        <div className={cx(styles.name)} data-test={agent?.fullName && 'name'}>
          {skeleton ? <Skeleton /> : agent?.fullName}
        </div>
        <span className={cx(styles.point, styles.location)} data-test="location">
          {skeleton ? <Skeleton /> : location || <ZeroWidthSpace />}
        </span>
        <Information />
      </div>
    </Link>
  );
};

export default PersonCard;
